import { HttpClient, HttpErrorResponse, HttpHeaders, HttpBackend } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable, tap, catchError, throwError, of, Subject } from 'rxjs';
import { Router } from '@angular/router';
import { UserCredentials } from '../../models/user-credentials.model';
import { getLoginUrl, getLogoutUrl, getValidateUrl, getRefreshUrl, getRegisterUrl } from '../../shared/utils/api-urls.utils';
import { environment } from '../../environments/environment';

// JwtRequest interface uesd to log in
interface JwtRequest {
  username: string;
  password: string;
}

// JwtResponse interface used to receive the JWT token
interface JwtResponse {
  token: string;
}

/** 
 * AuthService is responsible for handling authentication-related operations
 */
@Injectable({
  providedIn: 'root'
})
export class AuthService {
  /** Session storage key to store token in */
  private readonly TOKEN_KEY = 'token';

  /** Loaded token status */
  public loadedToken: boolean = false;

  /** JWT claims subject */
  private claimsSubject = new BehaviorSubject<Map<string, any> | null>(null);
  public claims$ = this.claimsSubject.asObservable();

  /** Authenticated subject */
  private authenticatedSubject = new BehaviorSubject<boolean>(false);
  public authenticated$ = this.authenticatedSubject.asObservable();

  /** Indicates if a token refresh is in progress */
  private refreshInProgressSubject = new BehaviorSubject<boolean>(false);
  public refreshInProgress$ = this.refreshInProgressSubject.asObservable();

  /** HttpClient instance for API requests */
  public http: HttpClient;

  /**
   * Constructor initializes services and loads the token from session storage 
   * @param http HttpClient instance for making HTTP requests
   */
  constructor(
    private _router: Router,
    private httpBackend: HttpBackend,
  ) {
    // Use traditional HttpClient if mockBackend is enabled in non-production
    if (!environment.production && environment.mockBackend) {
      this.http = inject(HttpClient);
    } else {
      // This HttpClient instance will NOT use interceptors
      const httpNoInterceptor = new HttpClient(this.httpBackend);
      this.http = httpNoInterceptor;
    }

    // Load the token from session storage
    if (!this._loadToken()) {
      this._clearTokenState();
      return;
    }
    this.loadedToken = true;

    // On successful load, check token validity
    console.log('[AUTH]: Token loaded successfully');

    // Validate the token if it loaded
    this.validateToken().subscribe({
      next: (isValid) => {
        if (isValid) {
          // If token is valid, parse and store claims
          console.log('[AUTH]: Token is valid, refreshing');
          this.refreshInProgressSubject.next(true);
          this._router.navigate(['/']);
          this.refreshToken();
        } else {
          // Otherwise, clear session and redirect to login
          console.log('[AUTH]: Token is invalid, clearing session');
          this.logout();
        }
      }
    });
  }

  /**
   * Sends login request and stores JWT on success
   * 
   * @param username - User's username
   * @param password - User's password
   * @returns Observable that emits the JWT response
   */
  login(username: string, password: string): Observable<any> {
    console.log('[AUTH]: Attempting to log in with username:', username);

    // Create request body
    const body: JwtRequest = { username, password };

    // Post to login endpoint
    return this.http.post<JwtResponse>(getLoginUrl(), body).pipe(
      // On success, set the token and parse claims
      tap((response) => {
        this._setToken(response.token);
      }),
      // Handle errors
      catchError(this._handleError('Login failed'))
    );
  }

  /** 
   * Clears the token and claims and hits the logout endpoint
   */
  logout(): void {
    console.log('[AUTH]: Logging out...');

    // If no refresh is in progress, proceed with logout immediately
    if (!this.refreshInProgressSubject.value) {
      this._performLogout();
      return;
    }

    // If a refresh is in progress, wait for it to complete before logging out
    console.log('[AUTH]: Token refresh in progress, waiting to complete before logout...');

    this.refreshInProgress$.subscribe({
      next: (refreshInProgress) => {
        if (!refreshInProgress) this._performLogout();
      }
    });
  }

  /**
   * Performs the actual logout operation
   */
  private _performLogout(): void {
    const headers: HttpHeaders = new HttpHeaders({
      'Authorization': `Bearer ${this.token}`
    });

    this._clearTokenState();

    // Hit the logout endpoint and clear session storage and claims regardless
    // We need to clear after so the auth interceptor still sends the token to logout
    this.http.post(getLogoutUrl(), null, { headers }).subscribe({
      next: () => {
        console.log('[AUTH]: Logout successful');
        this._router.navigate(['/login']);
      },
      error: (error: HttpErrorResponse) => {
        console.error('[AUTH]: Logout failed', error);
        this._router.navigate(['/login']);
      }
    });
  }

  /**
   * Validate that a given token is valid by requesting to the backend
   * 
   * @param token - JWT token to validate
   * @return Observable that emits true if token is valid, false otherwise
   */
  validateToken(): Observable<boolean> {
    // Hit the validate endpoint with the token
    return this.http.post<boolean>(getValidateUrl(), this.token).pipe(
      // Tap the response to update the authenticated subject
      tap((isValid: boolean) => {
        console.log('[AUTH]: Token validation response:', isValid);
        this.authenticatedSubject.next(isValid);
      }),
      // On error, return false
      catchError((error: HttpErrorResponse) => {
        // Return false to indicate invalid token
        console.error('[AUTH]: Token validation failed', error);
        return of(false);
      }),
    );
  }

  /**
   * Refresh the user's token
   * 
   * @return Observable that emits when the token is refreshed
   */
  refreshToken(): Observable<void> {
    console.log("[AUTH]: refreshing user token");
    this.refreshInProgressSubject.next(true);

    // Create a subject to return to the caller
    const subject = new Subject<void>();

    // Get the current token
    const token = this.token;

    // Attempt to refresh the token
    this.http.post<JwtResponse>(getRefreshUrl(), token).subscribe({
      next: (resp: JwtResponse) => {
        console.log("[AUTH]: token refreshed successfully");
        this._setToken(resp.token);
        this.refreshInProgressSubject.next(false);
        subject.next();
      },
      error: (error: any) => {
        console.error("[AUTH]: could not refresh token", error);
        this.refreshInProgressSubject.next(false);
        subject.error(error);
      }
    });

    return subject.asObservable();
  }

  /**
   * Register a new user with the backend system
   */
  register(credentials: UserCredentials): Observable<any> {
    console.log('[AUTH]: Attempting to register user:', credentials.username);

    // Post to register endpoint
    return this.http.post<UserCredentials>(getRegisterUrl(), credentials).pipe(
      tap((response: UserCredentials) => {
        console.log('[AUTH]: Registration successful');
      }),
      catchError(this._handleError('Registration failed'))
    );
  }


  /** 
     * Parses the token from session storage on init 
     * 
     * @return true if token was loaded successfully, false otherwise
     */
  private _loadToken(): boolean | null {
    console.log('[AUTH]: Loading token from session storage');

    const token = sessionStorage.getItem(this.TOKEN_KEY);
    if (token) {
      this._parseAndStoreClaims(token);
    }
    return token !== null;
  }

  /** 
   * Stores the token and parses claims 
   * 
   * @param token - JWT token to be stored
   */
  private _setToken(token: string) {
    sessionStorage.setItem(this.TOKEN_KEY, token);
    this.authenticatedSubject.next(true);
    this._parseAndStoreClaims(token);
  }

  /** 
   * Parses JWT and updates BehaviorSubject 
   * 
   * @param token - JWT token to parse
   */
  private _parseAndStoreClaims(token: string) {
    try {
      const payload = token.split('.')[1];
      const decoded = atob(payload);
      const claims = JSON.parse(decoded);

      console.log('[AUTH]: Parsed JWT claims:', claims);
      this.claimsSubject.next(new Map<string, any>(Object.entries(claims)));
    } catch (e) {
      console.error('[AUTH]: cannot parse JWT claims, invalid format', e);
    }
  }

  /**
   * Clear token state
   */
  private _clearTokenState() {
    console.log('[AUTH]: Clearing token state');

    sessionStorage.removeItem(this.TOKEN_KEY);
    this.authenticatedSubject.next(false);
    this.claimsSubject.next(null);
  }

  /** Returns the raw token */
  get token(): string | null {
    return sessionStorage.getItem(this.TOKEN_KEY);
  }

  /** Returns claims synchronously */
  get claims(): Map<string, any> | null {
    return this.claimsSubject.value;
  }

  /** Returns whether the user is authenticated */
  get isAuthenticated(): boolean {
    return this.authenticatedSubject.value;
  }

  /**
   * Helper that returns a function to handle HTTP errors
   */
  private _handleError(response: string) {
    return (error: HttpErrorResponse) => {
      console.error('[AUTH]: Authentication error', error);
      return throwError(() => error.error.message || new Error(response) || "An error occurred");
    }
  }
}
